/**
 * Home feed — active challenges, challenge winners, workout feed with filters.
 * Uses the design-system primitives; preserves all feed actions (verify, delete, view profile).
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { subscribeFeedFiltered, deleteWorkout } from '../../domain/usecases/workouts';
import { getVerifiedWorkoutIdsByUser, verifyWorkout } from '../../domain/usecases/verifications';
import { subscribeActiveChallenges } from '../../domain/usecases/challenges';
import { challengeSubmissionRepo } from '../../data/repositories/ChallengeSubmissionRepository';
import { subscribeLatestWinnerAnnouncements } from '../../data/repositories/ChallengeWinnerAnnouncementRepository';
import { userRepo } from '../../data/repositories/UserRepository';
import { useAuth } from '../auth/AuthProvider';
import PlateCard from '../../shared/ui/PlateCard.jsx';
import Button from '../../shared/ui/Button.jsx';
import IconButton from '../../shared/ui/IconButton.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Icon from '../../shared/ui/Icon.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Select from '../../shared/ui/Select.jsx';
import Display from '../../shared/ui/Display.jsx';
import Title from '../../shared/ui/Title.jsx';
import { CHALLENGE_CATEGORY_LABELS } from '../../domain/entities/Challenge';
import { shareWorkout } from '../../shared/utils/shareCard';
import styles from './HomePage.module.css';

const FEED_LIMIT = 20;
const EXERCISES = [
  'Back Squat', 'Front Squat', 'Bench Press', 'Deadlift', 'Overhead Press',
  'Clean', 'Snatch', 'Other',
];
const CATEGORY_ICON = { powerLifter: 'barbell', strongman: 'flame', bodyBuilder: 'user' };

function formatTimeLeft(endDate) {
  const end = new Date(endDate);
  const now = new Date();
  if (end <= now) return 'Ended';
  const d = Math.ceil((end - now) / 86400000);
  return d === 1 ? '1 day left' : `${d} days left`;
}

export function HomePage() {
  const { userId: currentUserId } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [submissionCounts, setSubmissionCounts] = useState({});
  const [winnerAnnouncements, setWinnerAnnouncements] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [users, setUsers] = useState({});
  const [verifiedSet, setVerifiedSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filterExercise, setFilterExercise] = useState('');
  const [filterDiscipline] = useState('');

  useEffect(() => {
    const unsub = subscribeActiveChallenges(10, setChallenges);
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = subscribeLatestWinnerAnnouncements(3, setWinnerAnnouncements);
    return () => unsub();
  }, []);

  useEffect(() => {
    if (challenges.length === 0) return;
    Promise.all(
      challenges.map(async (c) => {
        const list = await challengeSubmissionRepo.listByChallenge(c.id, 500);
        return [c.id, list.length];
      })
    ).then((pairs) => {
      const map = {};
      pairs.forEach(([id, count]) => { map[id] = count; });
      setSubmissionCounts(map);
    });
  }, [challenges]);

  useEffect(() => {
    setLoading(true);
    const unsub = subscribeFeedFiltered(
      FEED_LIMIT,
      { exercise: filterExercise || null, discipline: filterDiscipline || null },
      (items) => {
        setWorkouts(items);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [filterExercise, filterDiscipline]);

  useEffect(() => {
    const uids = [...new Set(workouts.map((w) => w.userId))];
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(
        uids.map(async (uid) => {
          if (cancelled) return;
          const user = await userRepo.getById(uid);
          if (user) map[uid] = user;
        })
      );
      if (!cancelled) setUsers((prev) => ({ ...prev, ...map }));
    })();
    return () => { cancelled = true; };
  }, [workouts]);

  useEffect(() => {
    if (!currentUserId || workouts.length === 0) return;
    let cancelled = false;
    getVerifiedWorkoutIdsByUser(currentUserId, workouts.map((w) => w.id)).then((set) => {
      if (!cancelled) setVerifiedSet(set);
    });
    return () => { cancelled = true; };
  }, [currentUserId, workouts]);

  const handleVerify = useCallback(
    async (workoutId) => {
      const workout = workouts.find((w) => w.id === workoutId);
      if (!workout || !currentUserId) return;
      const result = await verifyWorkout(workoutId, currentUserId, workout.userId);
      if (result.ok) setVerifiedSet((prev) => new Set([...prev, workoutId]));
    },
    [workouts, currentUserId]
  );

  const handleDeletePost = useCallback(
    async (workoutId) => {
      if (!currentUserId) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm('Delete this lift?')) return;
      await deleteWorkout(workoutId, currentUserId);
    },
    [currentUserId]
  );

  if (loading && workouts.length === 0) {
    return (
      <div className={styles.page}>
        <Display as="h1" size="l">Feed</Display>
        <div className={styles.skeletonList}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} width="100%" height={320} radius="var(--radius-md)" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Display as="h1" size="l">Feed</Display>

      {challenges.length > 0 && (
        <section className={styles.challengesSection}>
          <Title as="h2">Active challenges</Title>
          <div className={styles.challengesGrid}>
            {challenges.map((c) => (
              <Link key={c.id} to={`/challenges/${c.id}`} className={styles.challengeCard}>
                <span className={styles.challengeCardIcon}>
                  <Icon name={CATEGORY_ICON[c.category] || 'trophy'} size="md" />
                </span>
                <span className={styles.challengeCardTitle}>{c.title || c.exercise || 'Challenge'}</span>
                <span className={styles.challengeCardMeta}>
                  {CHALLENGE_CATEGORY_LABELS[c.category] || ''} · {(c.submissionCount ?? submissionCounts[c.id] ?? 0)} submissions
                </span>
                <span className={styles.challengeCardTime}>{formatTimeLeft(c.endDate || c.expiresAt)}</span>
                <span className={styles.challengeCardCta}>Tap to participate</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {winnerAnnouncements.length > 0 && (
        <section className={styles.challengesSection}>
          <Title as="h2">Challenge winners</Title>
          <div className={styles.challengesGrid}>
            {winnerAnnouncements.map((a) => (
              <div key={a.id} className={styles.winnerCard}>
                <span className={styles.winnerLabel}>{a.challengeTitle} · Winner</span>
                <div className={styles.winnerInfo}>
                  {a.winnerAvatarUrl ? (
                    <img src={a.winnerAvatarUrl} alt="" className={styles.winnerAvatar} />
                  ) : (
                    <span className={styles.winnerAvatarPlaceholder}>{a.winnerDisplayName?.charAt(0) || '?'}</span>
                  )}
                  <span className={styles.winnerName}>{a.winnerDisplayName}</span>
                  <span className={styles.winnerVotes}>{a.voteCount} votes</span>
                </div>
                {a.videoUrl && (
                  <video src={a.videoUrl} controls className={styles.winnerVideo} playsInline />
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className={styles.filters}>
        <Select
          label="Workout"
          value={filterExercise}
          onChange={(e) => setFilterExercise(e.target.value)}
          options={[{ value: '', label: 'All workouts' }, ...EXERCISES.map((ex) => ({ value: ex, label: ex }))]}
        />
      </div>

      <div className={styles.list}>
        {workouts.length === 0 ? (
          <EmptyState
            icon="flame"
            title="Feed is empty"
            body='No lifts here yet. Add a lift and enable "Show in feed" so others can see and verify it.'
          />
        ) : (
          workouts.map((workout) => {
            const user = users[workout.userId];
            const isOwn = workout.userId === currentUserId;
            const isVerified = verifiedSet.has(workout.id);
            const handleShare = () => shareWorkout({
              liftName: workout.exercise,
              weight: workout.weight,
              reps: workout.reps,
              athleteHandle: user?.handle,
              athleteDisplayName: user?.displayName,
              isPR: Boolean(workout.isPR),
              isVerified,
            }).catch(() => { /* user cancelled or browser blocked */ });
            const footer = (
              <>
                <IconButton icon="share" label="Share" onClick={handleShare} />
                {user && (
                  <Button variant="ghost" size="sm" as={Link} to={`/profile/${workout.userId}`}>
                    Profile
                  </Button>
                )}
                {!isOwn && (
                  isVerified
                    ? <Badge variant="gold">Verified</Badge>
                    : <Button variant="secondary" size="sm" onClick={() => handleVerify(workout.id)}>Verify</Button>
                )}
                {isOwn && (
                  <IconButton icon="trash" label="Delete lift" onClick={() => handleDeletePost(workout.id)} />
                )}
              </>
            );
            return (
              <PlateCard
                key={workout.id}
                mediaSrc={workout.videoUrl}
                mediaAlt={workout.exercise}
                mediaType="video"
                liftName={workout.exercise}
                weight={`${workout.weight} kg`}
                reps={workout.reps}
                athlete={user ? `@${user.handle || user.displayName || ''}` : ''}
                isPR={Boolean(workout.isPR)}
                footer={footer}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
